import { useCallback, useRef, useEffect } from "react";
import { Record, Pause, Stop, Trash, MicrophoneSlash } from "@phosphor-icons/react";
import { useRecorder } from "@/hooks/useRecorder";
import { uploadFile } from "@/lib/storage/upload";
import { createTake } from "@/lib/firestore/takes";
import { Card, Eyebrow } from "@/components/ui";

function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

interface RecorderPanelProps {
  projectId: string;
  nextOrder: number;
  onTakeCreated: () => void;
}

/**
 * RecorderPanel — DAW-style dictation lane.
 *
 * Paper-raised wrapper, walnut accents, mono labels. Waveform draws on a
 * paper-2 canvas (matches the surrounding tone, no dark mixer block).
 * Big record button: walnut on idle, flag-red while recording.
 */
export function RecorderPanel({ projectId, nextOrder, onTakeCreated }: RecorderPanelProps) {
  const recorder = useRecorder(projectId);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  // Canvas waveform — paper-2 background, walnut bars
  useEffect(() => {
    const canvas = canvasRef.current;
    const analyser = recorder.analyserNode;
    if (!canvas || !analyser || recorder.status !== "recording") return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      const root = getComputedStyle(document.documentElement);
      const paper2 = root.getPropertyValue("--as-paper-2").trim();
      const walnut = root.getPropertyValue("--as-walnut").trim();
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = paper2;
      ctx.fillRect(0, 0, w, h);

      const barWidth = (w / bufferLength) * 2.5;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * h * 0.8;
        const y = h - barHeight;
        ctx.fillStyle = walnut;
        ctx.globalAlpha = 0.3 + (dataArray[i] / 255) * 0.7;
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth - 1, barHeight, 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        x += barWidth;
      }
    };
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [recorder.analyserNode, recorder.status]);

  const handleStop = useCallback(async () => {
    const blob = await recorder.stop();
    if (!blob) return;
    const file = new File([blob], `recording-${Date.now()}.${recorder.fileExtension}`, { type: recorder.mimeType });
    const storagePath = await uploadFile(file, projectId, "audio");
    await createTake(projectId, {
      type: "recording",
      title: `Take ${nextOrder + 1}`,
      duration_seconds: recorder.elapsedSeconds,
      storage_path: storagePath,
      format: recorder.fileExtension,
      mime_type: recorder.mimeType,
      size_bytes: blob.size,
      status: "ready",
      order: nextOrder,
    });
    onTakeCreated();
  }, [recorder, projectId, nextOrder, onTakeCreated]);

  if (!recorder.isSupported) {
    return (
      <Card className="text-center py-12">
        <MicrophoneSlash size={40} weight="duotone" className="mx-auto text-ink-4" />
        <p className="mt-3 font-text text-t-16 text-ink-3">This browser cannot capture the source.</p>
        <p className="mt-1 font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-4">
          Try Chrome, Firefox, or Safari 14.5+.
        </p>
      </Card>
    );
  }

  const isActive = recorder.status === "recording" || recorder.status === "paused";

  const statusLabel =
    recorder.status === "recording" ? "Capturing source" :
    recorder.status === "paused"    ? "Source paused" :
    recorder.status === "saving"    ? "Saving source" :
    recorder.status === "requesting"? "Opening microphone" :
    recorder.status === "error"     ? (recorder.error ?? "Capture failed") :
    "Ready for source";

  const statusColor =
    recorder.status === "recording" ? "text-flag" :
    recorder.status === "paused"    ? "text-walnut-soft" :
    recorder.status === "saving"    ? "text-walnut" :
    recorder.status === "error"     ? "text-flag" :
    "text-ink-3";

  return (
    <Card className="p-5">
      {/* Status eyebrow */}
      <div className="mb-3 text-center">
        <span className={`font-mono text-[11px] tracking-as-eyebrow uppercase ${statusColor}`}>
          {statusLabel}
        </span>
      </div>

      {/* Waveform */}
      <canvas
        ref={canvasRef}
        width={600}
        height={54}
        className="w-full bg-paper-2 rounded-as-2"
        style={{ height: 54 }}
      />

      {/* Timer */}
      <div className="my-4 text-center">
        <span className="font-mono text-[2rem] font-semibold text-ink-1 tracking-as-snug tabular-nums">
          {formatTimer(recorder.elapsedSeconds)}
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        {!isActive && recorder.status !== "saving" && (
          <button
            onClick={recorder.start}
            className="flex h-14 w-14 items-center justify-center rounded-as-pill bg-walnut text-paper-raised shadow-as-2 hover:bg-walnut-deep active:shadow-as-press transition-colors duration-as-1"
            aria-label="Start source capture"
          >
            <Record size={28} weight="fill" />
          </button>
        )}

        {recorder.status === "recording" && (
          <button
            onClick={recorder.pause}
            className="flex h-11 w-11 items-center justify-center rounded-as-pill bg-paper-2 text-ink-2 hover:bg-paper-3 transition-colors duration-as-1"
            aria-label="Pause source capture"
          >
            <Pause size={22} weight="fill" />
          </button>
        )}

        {recorder.status === "paused" && (
          <button
            onClick={recorder.resume}
            className="flex h-14 w-14 items-center justify-center rounded-as-pill bg-walnut text-paper-raised shadow-as-2 hover:bg-walnut-deep transition-colors duration-as-1"
            aria-label="Resume source capture"
          >
            <Record size={28} weight="fill" />
          </button>
        )}

        {isActive && (
          <>
            <button
              onClick={handleStop}
              className="flex h-11 w-11 items-center justify-center rounded-as-pill bg-ink-1 text-paper-raised hover:bg-walnut-deep transition-colors duration-as-1"
              aria-label="Stop source capture"
            >
              <Stop size={22} weight="fill" />
            </button>
            <button
              onClick={recorder.discard}
              className="flex h-10 w-10 items-center justify-center rounded-as-pill text-ink-3 hover:bg-flag-tint hover:text-flag transition-colors duration-as-1"
              aria-label="Discard source capture"
            >
              <Trash size={18} />
            </button>
          </>
        )}
      </div>

      {!isActive && recorder.status === "idle" && (
        <p className="mt-4 text-center font-text text-t-13 text-ink-3">
          Press once and speak the book into the room. PullPress saves every 30 seconds.
        </p>
      )}

      {!isActive && recorder.status === "idle" && (
        <div className="mt-2 text-center">
          <Eyebrow>Source {nextOrder + 1}</Eyebrow>
        </div>
      )}
    </Card>
  );
}
